import type {
  Cart,
  CartItem,
  CartItemInput,
  SHOPIFY_ENV,
} from './storefront.types';
import {
  addToCart,
  createCart,
  getCart,
  updateCartLines,
} from './storefrontClient';

type CreateUpdateCart = {
  cart: Cart;
  cartId: string;
};

type AttributeLike = {
  key?: string | null;
  value?: string | null;
};

const normalizeAttributes = (
  attributes: AttributeLike[] | null | undefined
): { key: string; value: string }[] =>
  (attributes ?? [])
    .flatMap(({ key, value }) =>
      typeof key === 'string' && typeof value === 'string'
        ? [{ key, value }]
        : []
    )
    .sort(
      (left, right) =>
        left.key.localeCompare(right.key) ||
        left.value.localeCompare(right.value)
    );

const hasSameAttributes = (
  lineAttributes: CartItem['attributes'],
  itemAttributes: CartItemInput['attributes']
) => {
  const normalizedLineAttributes = normalizeAttributes(lineAttributes);
  const normalizedItemAttributes = normalizeAttributes(itemAttributes);

  if (normalizedLineAttributes.length !== normalizedItemAttributes.length) {
    return false;
  }

  return normalizedLineAttributes.every(
    ({ key, value }, index) =>
      key === normalizedItemAttributes[index]?.key &&
      value === normalizedItemAttributes[index]?.value
  );
};

const doesCartLineMatchItem = (line: CartItem, item: CartItemInput) =>
  line.merchandise.id === item.merchandiseId &&
  (line.sellingPlanAllocation?.sellingPlan.id ?? undefined) ===
    item.sellingPlanId &&
  hasSameAttributes(line.attributes, item.attributes);

const createNewCart = async (
  item: CartItemInput,
  storefrontEnv: SHOPIFY_ENV
): Promise<CreateUpdateCart | null> => {
  const newCart = await createCart([item], storefrontEnv);
  if (newCart) {
    return { cart: newCart as Cart, cartId: newCart.id };
  } else {
    console.error(
      'Error creating cart, the returned cart is null or undefined'
    );
    return null;
  }
};

const updateItem = async (
  existingCart: Cart,
  item: CartItemInput,
  cartId: string,
  storefrontEnv: SHOPIFY_ENV
) => {
  const existingLine = existingCart?.lines.edges.find((line) =>
    doesCartLineMatchItem(line.node, item)
  );
  let updatedCart: Cart | null | undefined;
  if (existingLine) {
    const updatedQuantity = existingLine.node.quantity + item.quantity;
    updatedCart = await updateCartLines(
      cartId,
      [{ id: existingLine.node.id, quantity: updatedQuantity }],
      storefrontEnv
    );
  } else {
    updatedCart = await addToCart(cartId, [item], storefrontEnv);
  }
  if (updatedCart) {
    return { cart: updatedCart, cartId };
  } else {
    console.error('Existing Cart could not be updated');
    return { cart: existingCart, cartId };
  }
};

export async function updateCart(
  cartId: string | null,
  item: CartItemInput,
  storefrontEnv: SHOPIFY_ENV
): Promise<CreateUpdateCart | null> {
  if (!cartId) {
    return createNewCart(item, storefrontEnv);
  } else {
    let existingCart: Cart | null | undefined;

    try {
      existingCart = await getCart(cartId, storefrontEnv);

      if (
        existingCart?.updatedAt &&
        isStaleCart(existingCart.updatedAt as string)
      ) {
        const refreshedCart = createNewCart(item, storefrontEnv);
        if (refreshedCart) {
          return refreshedCart;
        }
      }
    } catch {
      const newCartOnError = createNewCart(item, storefrontEnv);
      if (newCartOnError) {
        return newCartOnError;
      }
    }

    try {
      if (existingCart) {
        return updateItem(existingCart, item, cartId, storefrontEnv);
      } else {
        const newCart = createNewCart(item, storefrontEnv);
        return newCart;
      }
    } catch (_e) {
      console.error('Could not create new cart or update existing Cart');
      return null;
    }
  }
}

export function quickCheckout(variantGid: string, quantity: number): void {
  const variantId = variantGid?.split('/').pop() || '';
  window.location.href = `https://shop.sunrun.com/cart/${variantId}:${quantity}`;
}

export const isStaleCart = (updatedAt: string): boolean => {
  const ONE_DAY_IN_MS = 1000 * 60 * 60 * 24;

  const updateTime = new Date(updatedAt).getTime();
  const currentTime = Date.now();

  const ageMs = currentTime - updateTime;
  return ageMs >= ONE_DAY_IN_MS;
};
